import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';

// Full Tinyme-inspired palette: pastels + vibrants, organised by hue.
// First 8 entries form the "quick access" row; the rest unfold under "Plus de couleurs".
export const PINGME_COLORS = [
  // Quick access (first 8) - most popular pastel choices
  { hex: '#FCE4EC', label: 'Rose poudre' },
  { hex: '#FFE0B2', label: 'Creme peche' },
  { hex: '#FFE082', label: 'Or pastel' },
  { hex: '#D0E6C2', label: 'Vert mint' },
  { hex: '#B3E5FC', label: 'Bleu ciel' },
  { hex: '#D1C4E9', label: 'Lilas' },
  { hex: '#C9B59A', label: 'Sable' },
  { hex: '#5D4037', label: 'Cafe' },
  // Neutrals
  { hex: '#FFFFFF', label: 'Blanc' },
  { hex: '#BDBDBD', label: 'Gris clair' },
  { hex: '#616161', label: 'Gris fonce' },
  { hex: '#212121', label: 'Noir' },
  // Pinks
  { hex: '#F8BBD0', label: 'Rose tendre' },
  { hex: '#F48FB1', label: 'Rose pivoine' },
  { hex: '#EC407A', label: 'Rose vif' },
  { hex: '#AD1457', label: 'Framboise fonce' },
  // Reds
  { hex: '#EF5350', label: 'Rouge corail' },
  { hex: '#E53935', label: 'Rouge vif' },
  { hex: '#B71C1C', label: 'Rouge sombre' },
  { hex: '#8E4858', label: 'Bordeaux' },
  // Oranges
  { hex: '#FFAB91', label: 'Saumon clair' },
  { hex: '#FF8A65', label: 'Corail' },
  { hex: '#FB8C00', label: 'Orange vif' },
  { hex: '#E64A19', label: 'Orange brule' },
  // Yellows / mustards
  { hex: '#FFF59D', label: 'Jaune pale' },
  { hex: '#FFD54F', label: 'Or' },
  { hex: '#F8C765', label: 'Moutarde claire' },
  { hex: '#E5A53A', label: 'Moutarde' },
  // Browns / earth
  { hex: '#D7B899', label: 'Beige' },
  { hex: '#A28066', label: 'Taupe' },
  { hex: '#8D6E63', label: 'Brun chocolat' },
  { hex: '#6D4C41', label: 'Brun fonce' },
  // Greens
  { hex: '#C5E1A5', label: 'Vert tendre' },
  { hex: '#B5CFB0', label: 'Sauge' },
  { hex: '#97A97C', label: 'Vert olive' },
  { hex: '#66BB6A', label: 'Vert kelly' },
  { hex: '#388E3C', label: 'Vert sapin' },
  { hex: '#1B5E20', label: 'Vert profond' },
  // Teals
  { hex: '#80DEEA', label: 'Turquoise clair' },
  { hex: '#26C6DA', label: 'Turquoise' },
  { hex: '#00838F', label: 'Sarcelle' },
  { hex: '#004D40', label: 'Sarcelle profonde' },
  // Blues
  { hex: '#90CAF9', label: 'Bleu pastel' },
  { hex: '#42A5F5', label: 'Bleu clair' },
  { hex: '#1E88E5', label: 'Bleu' },
  { hex: '#1565C0', label: 'Bleu fonce' },
  { hex: '#0D47A1', label: 'Bleu marine' },
  // Purples
  { hex: '#B39DDB', label: 'Mauve clair' },
  { hex: '#9575CD', label: 'Mauve' },
  { hex: '#7E57C2', label: 'Violet' },
  { hex: '#4527A0', label: 'Violet profond' },
];

// Quick access count
const QUICK_COUNT = 8;

// Compute relative luminance to decide if checkmark should be black or white
function _luminance(hex) {
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16) / 255;
  const g = parseInt(h.substring(2, 4), 16) / 255;
  const b = parseInt(h.substring(4, 6), 16) / 255;
  const ch = (c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  return 0.2126 * ch(r) + 0.7152 * ch(g) + 0.0722 * ch(b);
}

const Swatch = ({ c, value, onChange }) => {
  const selected = (value || '').toLowerCase() === c.hex.toLowerCase();
  const isLight = _luminance(c.hex) > 0.6;
  const checkColor = isLight ? '#0f172a' : '#fff';
  return (
    <button
      key={c.hex}
      type="button"
      title={c.label}
      data-testid={`color-swatch-${c.hex.replace('#', '').toLowerCase()}`}
      onClick={() => onChange(c.hex)}
      className={`relative aspect-square rounded-lg border-2 transition-all hover:scale-110 ${
        selected ? 'border-charcoal scale-110 shadow-brutal-sm' : 'border-charcoal/20'
      }`}
      style={{ backgroundColor: c.hex }}
    >
      {selected && (
        <span className="absolute inset-0 flex items-center justify-center" style={{ color: checkColor }}>
          <svg viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        </span>
      )}
    </button>
  );
};

const NoneSwatch = ({ value, onChange }) => (
  <button
    type="button"
    title="Aucun"
    data-testid="color-swatch-none"
    onClick={() => onChange('')}
    className={`relative aspect-square rounded-lg border-2 transition-all hover:scale-110 bg-white overflow-hidden ${
      !value ? 'border-charcoal scale-110' : 'border-charcoal/20'
    }`}
  >
    <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-charcoal/40">aucun</span>
    <span className="absolute top-1/2 left-0 right-0 h-px bg-danger rotate-45" />
  </button>
);

const ColorPicker = ({ value, onChange, label, testid }) => {
  const [expanded, setExpanded] = useState(false);
  const quick = PINGME_COLORS.slice(0, QUICK_COUNT);
  const extra = PINGME_COLORS.slice(QUICK_COUNT);
  const extraCount = extra.length;
  // If the user already has a non-quick color selected, auto-expand
  const valueLower = (value || '').toLowerCase();
  const isExtraSelected = extra.some((c) => c.hex.toLowerCase() === valueLower);
  const isOpen = expanded || isExtraSelected;

  return (
    <div data-testid={testid || 'color-picker'}>
      {label && (
        <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-1.5 block">{label}</label>
      )}
      {/* Quick access row */}
      <div className="grid grid-cols-6 sm:grid-cols-9 gap-1.5">
        {quick.map((c) => (
          <Swatch key={c.hex} c={c} value={value} onChange={onChange} />
        ))}
        <NoneSwatch value={value} onChange={onChange} />
      </div>

      {/* Toggle for the rest of the palette */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        data-testid="color-picker-expand-toggle"
        className="mt-2 flex items-center gap-1 text-[11px] font-bold tracking-[0.08em] uppercase text-charcoal/70 hover:text-charcoal transition-colors"
      >
        <ChevronDown
          className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          strokeWidth={2.5}
        />
        {isOpen ? 'Moins de couleurs' : `Plus de couleurs (+${extraCount})`}
      </button>

      {/* Expanded palette - the "deployable" element */}
      <div
        className={`overflow-hidden transition-all duration-300 ease-out ${isOpen ? 'max-h-[600px] mt-2 opacity-100' : 'max-h-0 opacity-0'}`}
        data-testid="color-picker-expanded"
      >
        <div className="grid grid-cols-6 sm:grid-cols-9 gap-1.5 p-2 rounded-lg bg-surface-alt/60 border border-charcoal/10">
          {extra.map((c) => (
            <Swatch key={c.hex} c={c} value={value} onChange={onChange} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ColorPicker;
